import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Clock, Users, Bus, ArrowRight, Flame } from "lucide-react";
import { useT } from "../lib/i18n";

export default function BookingSearch() {
  const t = useT();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [form, setForm] = useState({
    from_place: sp.get("from_place") || "Pune",
    to_place: sp.get("to_place") || "Mumbai",
    travel_date: sp.get("travel_date") || new Date().toISOString().slice(0,10),
    passengers: parseInt(sp.get("passengers")) || 1,
    ac: sp.get("ac") || "any",
  });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const doSearch = async (e) => {
    if (e) e.preventDefault();
    setLoading(true); setSearched(true);
    try {
      const payload = { from_place: form.from_place, to_place: form.to_place, travel_date: form.travel_date, passengers: parseInt(form.passengers), ac: form.ac==="any" ? null : form.ac==="true" };
      const r = await api.post("/routes/search", payload);
      setResults(r.data);
    } catch (err) { console.error("Route search failed:", err); }
    setLoading(false);
  };
  useEffect(() => { doSearch(); }, []);

  const bookVehicle = () => {
    const q = new URLSearchParams({ ...form, booking_type: "vehicle_hire" }).toString();
    nav(`/book/form?${q}`);
  };
  const bookSeat = (route) => {
    const q = new URLSearchParams({ ...form, route_id: route.id, booking_type: "daily_route" }).toString();
    nav(`/book/form?${q}`);
  };

  return (
    <PublicLayout>
      <section className="max-w-6xl mx-auto px-4 pt-10 pb-16">
        <form onSubmit={doSearch} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 grid md:grid-cols-6 gap-3" data-testid="search-form">
          <div><Label className="text-xs">From</Label><Input value={form.from_place} onChange={e=>setForm({...form,from_place:e.target.value})} data-testid="search-from" /></div>
          <div><Label className="text-xs">To</Label><Input value={form.to_place} onChange={e=>setForm({...form,to_place:e.target.value})} data-testid="search-to" /></div>
          <div><Label className="text-xs">Date</Label><Input type="date" value={form.travel_date} onChange={e=>setForm({...form,travel_date:e.target.value})} data-testid="search-date" /></div>
          <div><Label className="text-xs">Passengers</Label><Input type="number" min="1" value={form.passengers} onChange={e=>setForm({...form,passengers:e.target.value})} data-testid="search-pax" /></div>
          <div><Label className="text-xs">AC</Label>
            <Select value={form.ac} onValueChange={v=>setForm({...form,ac:v})}>
              <SelectTrigger data-testid="search-ac"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="any">Any</SelectItem><SelectItem value="true">AC</SelectItem><SelectItem value="false">Non-AC</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="flex items-end"><Button type="submit" className="w-full bg-[#ea580c] hover:bg-orange-700" data-testid="search-submit">Search</Button></div>
        </form>

        <div className="mt-8 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-xl font-display font-bold">{t("book.tab_daily")}</h2>
            {loading && <div className="text-sm text-slate-500">…</div>}
            {!loading && searched && results.length===0 && (
              <div className="p-6 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900">{t("book.no_results")}</div>
            )}
            {results.map(r => {
              const free = r.available_seats ?? 0;
              const cap = r.seat_capacity || 1;
              const pct = Math.max(0, Math.min(100, Math.round((free / cap) * 100)));
              const ribbon = free === 0
                ? { cls: "bg-rose-600 text-white", label: "Full — join waitlist" }
                : free <= 2
                  ? { cls: "bg-amber-500 text-white", label: `${free} seat${free===1?"":"s"} left — hurry!` }
                  : free <= 5
                    ? { cls: "bg-orange-500 text-white", label: `${free} seats left` }
                    : { cls: "bg-emerald-600 text-white", label: `${free} seats available` };
              return (
              <div key={r.id} className={`relative p-5 rounded-xl bg-white border ${r.popular ? "border-amber-400 ring-2 ring-amber-200" : "border-slate-200"} flex flex-col md:flex-row md:items-center gap-4 overflow-hidden`} data-testid={`search-result-${r.id}`}>
                {r.popular && (
                  <div className="absolute -left-1 top-3 z-10 flex items-center gap-1 pl-2 pr-3 py-1 rounded-r-full bg-gradient-to-r from-amber-500 to-orange-600 text-white text-[10px] font-black tracking-wider shadow-lg" data-testid={`popular-badge-${r.id}`}>
                    <Flame className="w-3 h-3 animate-pulse" /> MOST BOOKED TODAY
                  </div>
                )}
                <div className={`absolute -right-8 top-3 rotate-45 text-[10px] font-black tracking-wider px-10 py-0.5 shadow-md ${ribbon.cls}`} data-testid={`seat-ribbon-${r.id}`}>{free===0 ? "FULL" : free<=2 ? "HURRY" : free<=5 ? "FILLING" : "AVAILABLE"}</div>
                <div className={`flex-1 ${r.popular ? "pt-6" : ""}`}>
                  <div className="font-display font-bold text-lg">{r.name}</div>
                  <div className="text-sm text-slate-500">{r.from_place} → {r.to_place}</div>
                  <div className="mt-2 flex flex-wrap gap-3 text-xs">
                    <span className="inline-flex items-center gap-1 text-slate-600"><Clock className="w-3 h-3 text-[#ea580c]" /> {r.departure_time}</span>
                    <span className="inline-flex items-center gap-1 text-slate-600"><Bus className="w-3 h-3 text-[#ea580c]" /> {r.vehicle_type}</span>
                    <span className="inline-flex items-center gap-1 text-slate-600"><Users className="w-3 h-3 text-[#ea580c]" /> {free}/{cap} seats free</span>
                    <span className="px-2 rounded-full bg-slate-100">{r.ac ? "AC" : "Non-AC"}</span>
                  </div>
                  <div className="mt-3 flex items-center gap-2" title={`${free} of ${cap} seats available`}>
                    <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden" data-testid={`seat-bar-${r.id}`}>
                      <div className={`h-full rounded-full transition-all ${free===0?"bg-rose-500":free<=2?"bg-amber-500":free<=5?"bg-orange-500":"bg-emerald-500"}`} style={{width:`${pct}%`}} />
                    </div>
                    <span className={`text-[11px] font-bold ${free===0?"text-rose-600":free<=2?"text-amber-600":free<=5?"text-orange-600":"text-emerald-700"}`}>{ribbon.label}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-black text-[#1e1b4b]">₹{r.price_per_seat}<span className="text-xs font-normal text-slate-500">/seat</span></div>
                  <Button onClick={()=>bookSeat(r)} disabled={free<form.passengers} className="mt-2 bg-[#ea580c] hover:bg-orange-700" data-testid={`book-seat-${r.id}`}>{free<form.passengers?"Full":"Book Seat"} <ArrowRight className="w-3 h-3 ml-1" /></Button>
                </div>
              </div>
            );})}
          </div>
          <aside>
            <div className="p-6 rounded-2xl bg-[#1e1b4b] text-white sticky top-24">
              <h3 className="font-display font-bold text-xl">Or book a full vehicle</h3>
              <p className="text-sm text-white/80 mt-2">For private trips, weddings, corporate, outstation &amp; multi-day rides — we&apos;ll allocate a suitable vehicle &amp; driver.</p>
              <Button onClick={bookVehicle} className="mt-4 w-full bg-[#ea580c] hover:bg-orange-700" data-testid="book-vehicle-flow">Book Vehicle <ArrowRight className="w-4 h-4 ml-1" /></Button>
              <Link to="/tours" className="mt-3 block text-center text-sm underline text-white/80" data-testid="link-tours">Explore group tours →</Link>
            </div>
          </aside>
        </div>
      </section>
    </PublicLayout>
  );
}
